import { useState, useEffect } from "react";
import { Loader2, Trash2, PowerOff, Power, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Reusable confirmation dialog for lifecycle actions.
 *
 * Props:
 * - open: boolean
 * - onOpenChange
 * - action: "delete" | "deactivate" | "reactivate" | "restore"
 * - recordLabel: what the user sees inside the message ("Course \"BLS Certification\"")
 * - requireReason: bool (auto-enabled for delete; optional for others)
 * - loading: bool
 * - onConfirm: (reason) => Promise
 */
export function LifecycleConfirmDialog({
  open, onOpenChange, action, recordLabel = "this record",
  requireReason, loading = false, onConfirm,
}) {
  const [reason, setReason] = useState("");

  useEffect(() => { if (!open) setReason(""); }, [open]);

  const meta = {
    delete: {
      title: "Delete record",
      icon: Trash2,
      description: `Are you sure you want to delete ${recordLabel}? It will be removed from active lists and will not be visible on the portal.`,
      button: "Yes, Delete",
      tone: "danger",
      needsReason: true,
    },
    deactivate: {
      title: "Deactivate record",
      icon: PowerOff,
      description: `Deactivate ${recordLabel}? It will be hidden publicly and cannot be selected in new mappings. Existing historical records remain unchanged.`,
      button: "Yes, Deactivate",
      tone: "warn",
      needsReason: false,
    },
    reactivate: {
      title: "Reactivate record",
      icon: Power,
      description: `Are you sure you want to reactivate ${recordLabel}? It will be available again in relevant lists and dropdowns.`,
      button: "Yes, Reactivate",
      tone: "primary",
      needsReason: false,
    },
    restore: {
      title: "Restore deleted record",
      icon: RotateCcw,
      description: `Restore ${recordLabel}? It will come back as Inactive — you can then Reactivate it when ready.`,
      button: "Yes, Restore",
      tone: "primary",
      needsReason: false,
    },
  }[action] || {};

  const Icon = meta.icon;
  const isReasonRequired = requireReason ?? meta.needsReason;
  const canSubmit = !loading && (!isReasonRequired || reason.trim().length >= 3);
  const toneClass = {
    danger: "bg-rose-600 hover:bg-rose-700",
    warn: "bg-amber-600 hover:bg-amber-700",
    primary: "bg-[#101876] hover:bg-[#0c135c]",
  }[meta.tone] || "bg-[#101876] hover:bg-[#0c135c]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`lifecycle-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon ? <Icon className="h-5 w-5" /> : null}
            {meta.title}
          </DialogTitle>
          <DialogDescription>{meta.description}</DialogDescription>
        </DialogHeader>
        {isReasonRequired && (
          <div className="space-y-1.5">
            <Label htmlFor="lifecycle-reason">Reason <span className="text-rose-600">*</span></Label>
            <Textarea
              id="lifecycle-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate entry / no longer offered / policy change"
              data-testid="lifecycle-reason-input"
            />
            <p className="text-[10px] text-slate-500">
              Minimum 3 characters. Stored in the audit log for compliance.
            </p>
          </div>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="lifecycle-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm?.(reason.trim())}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-70 ${toneClass}`}
            data-testid="lifecycle-confirm"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (Icon ? <Icon className="h-4 w-4" /> : null)}
            {meta.button}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
